import { useEffect, useState } from "react";
import {
  GESTION_TASKS,
  PAY_BRACKETS,
  TIME_COST_TASKS,
  computeTimeCost,
  computeTimeCostFromTasks,
  tachesDeGestion,
  gestionDejaComptee,
} from "@riseafrica/scoring";
import { fmt } from "../lib/format";
import { track } from "../lib/track";
import { useApp } from "../state/AppState";
import { Fil } from "../components/Fil";

/**
 * Volet 02 — coût du temps.
 *
 * Il chiffre **les tâches du volet 1** quand elles existent, avec les heures
 * déjà déclarées, et ne redemande que la rémunération. Auparavant il présentait
 * six tâches génériques et figées : la personne qui venait de déclarer huit
 * heures sur « rapprochement de pièces » se voyait redemander son temps sur
 * « saisie et recopie de données ». Deux fois la même question, et une réponse
 * qui portait à côté — le montant annoncé ne reposait pas sur son métier.
 *
 * La liste générique reste le chemin d'entrée directe : chaque volet doit
 * pouvoir se prendre en premier, et un visiteur qui arrive ici sans être passé
 * par le volet 1 n'a pas de tâches à lui.
 */
export function Volet2() {
  useEffect(() => {
    track("volet2_start");
  }, []);

  const { goTo, setT2, say, t1, aSonCompte } = useApp();
  const tachesReelles = t1?.taches ?? [];
  const surMesure = tachesReelles.length > 0;

  const [heures, setHeures] = useState<number[]>(() =>
    surMesure ? tachesReelles.map((t) => t.heures ?? 3) : TIME_COST_TASKS.map(() => 3),
  );
  const [remuneration, setRemuneration] = useState(220000);
  /*
    La gestion de l'activité — T38, 24/09/2026.

    Seulement pour quelqu'un à son compte qui vient du volet 1 : c'est lui que
    le rapport servira, et sans ses tâches de métier il n'y a pas de rapport à
    rédiger. Les curseurs partent de zéro — un nombre posé d'avance serait une
    déclaration qu'il n'a pas faite, et il gonflerait le montant affiché.
  */
  const avecGestion = aSonCompte && surMesure;
  const [heuresGestion, setHeuresGestion] = useState<number[]>(() => GESTION_TASKS.map(() => 0));
  // Ce que les tâches du métier comptent déjà : le déclarer ici le compterait
  // deux fois (un graphiste a « Préparer le devis et la facture client » dans
  // son métier, et « Devis et facturation » plus bas).
  const couvertes = gestionDejaComptee(tachesReelles.map((t) => t.texte));
  const gestionRetenue = heuresGestion.map((h, i) => (couvertes[i] === null ? h : 0));

  const libelles = surMesure ? tachesReelles.map((t) => t.texte) : TIME_COST_TASKS.map((t) => t.name);

  const calculer = () => {
    const metier = tachesReelles.map((t, i) => ({ texte: t.texte, niveau: t.niveau, heures: heures[i] }));
    const resultatMetier = surMesure
      ? computeTimeCostFromTasks(metier, remuneration)
      : computeTimeCost(heures, remuneration);
    // Même formule, même barème : la gestion s'ajoute aux lignes, elle ne
    // change rien au calcul de celles du métier — ni à l'indice, qui ne la voit pas.
    const gestion = avecGestion ? tachesDeGestion(gestionRetenue) : [];
    const resultat =
      gestion.length > 0 ? computeTimeCostFromTasks([...metier, ...gestion], remuneration) : resultatMetier;
    setT2({
      ...resultat,
      ...(gestion.length > 0
        ? { heuresGestion: gestionRetenue, moneyMetier: resultatMetier.money }
        : {}),
    });
    say(
      `${resultat.hours} heures récupérables par mois, soit ${resultat.money.toLocaleString("fr-FR")} francs par an`,
    );
    track("volet2_done");
    goTo("s-r2");
  };

  return (
    <section>
      <div className="shell narrow app">
        <Fil etape={2} />
        <h2>Le coût de votre temps</h2>
        <p className="sub">
          {surMesure
            ? "Voici vos tâches et le temps que vous y avez déclaré. Ajustez si l'estimation vous paraît fausse, puis indiquez votre rémunération."
            : "Combien d'heures par semaine passez-vous sur chacune de ces tâches ? Une estimation suffit."}
        </p>

        <div>
          {libelles.map((nom, i) => (
            <div className="slider" key={`${nom}-${i}`}>
              <div className="top">
                <span className="name" id={`slider-${i}`}>
                  {nom}
                </span>
                {/* La virgule, pas le point : « 4.5 h » n'est pas français, et
                    tout ce produit l'est. Les heures viennent des durées
                    déclarées tâche par tâche, donc elles tombent souvent sur une
                    demie. */}
                <span className="val">{fmt(heures[i])} h</span>
              </div>
              <input
                type="range"
                min={0}
                max={20}
                value={heures[i]}
                aria-labelledby={`slider-${i}`}
                aria-valuetext={`${fmt(heures[i])} heures par semaine`}
                onChange={(e) =>
                  setHeures((h) => h.map((v, j) => (j === i ? Number(e.target.value) : v)))
                }
              />
            </div>
          ))}
        </div>

        {avecGestion && (
          <div role="group" aria-labelledby="gestion-titre" style={{ marginTop: "26px" }}>
            <h3 id="gestion-titre">Gérer votre activité</h3>
            <p className="sub">
              Le temps qu'il vous faut pour faire tourner votre affaire : il ne se vend pas, et il
              n'entre pas dans votre indice. Laissez à zéro ce qui ne vous concerne pas.
            </p>
            {GESTION_TASKS.map((tache, i) =>
              couvertes[i] !== null ? (
                <div className="slider" key={tache.name}>
                  <div className="top">
                    <span className="name">{tache.name}</span>
                    <span className="val">déjà compté</span>
                  </div>
                  <p className="fine" style={{ margin: "4px 0 0" }}>
                    Dans votre tâche « {couvertes[i]} », plus haut : on ne la compte pas deux fois.
                  </p>
                </div>
              ) : (
              <div className="slider" key={tache.name}>
                <div className="top">
                  <span className="name" id={`gestion-${i}`}>
                    {tache.name}
                  </span>
                  <span className="val">{fmt(heuresGestion[i])} h</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={20}
                  value={heuresGestion[i]}
                  aria-labelledby={`gestion-${i}`}
                  aria-valuetext={`${fmt(heuresGestion[i])} heures par semaine`}
                  onChange={(e) =>
                    setHeuresGestion((h) => h.map((v, j) => (j === i ? Number(e.target.value) : v)))
                  }
                />
              </div>
              ),
            )}
          </div>
        )}

        <div className="field" style={{ marginTop: "22px" }}>
          <label htmlFor="pay">
            {aSonCompte
              ? "Ce que votre activité vous laisse par mois, en moyenne"
              : "Ce qui vous reste par mois, à peu près"}
          </label>
          <select
            id="pay"
            value={remuneration}
            onChange={(e) => setRemuneration(Number(e.target.value))}
          >
            {PAY_BRACKETS.map((tranche) => (
              <option key={tranche.valeur} value={tranche.valeur}>
                {tranche.libelle}
              </option>
            ))}
          </select>
        </div>

        {/* Laiton comme sur les écrans de résultat : le geste qui fait avancer
              dans le parcours doit avoir le même poids partout. Il était
              secondaire sur les trois écrans de saisie et primaire sur les
              trois écrans de résultat — la même intention changeait de rang
              d'un écran à l'autre. */}
        <button type="button" className="btn btn-brass" onClick={calculer}>
          Calculer ce que ça me coûte
        </button>
        <p className="hint">Par tranche, sans aucune donnée nominative.</p>

        {/* Le retour dit où il mène. Il ramenait à l'accueil même au milieu du
            parcours, ce qui effaçait le fil qu'on venait de recoudre. */}
        {t1 ? (
          <button type="button" className="back" onClick={() => goTo("s-r1")}>
            ← Revenir à mon indice
          </button>
        ) : (
          <button type="button" className="back" onClick={() => goTo("home")}>
            ← Revenir au diagnostic
          </button>
        )}
      </div>
    </section>
  );
}
